import { InlineTitleEditor } from '@core/component/InlineTitleEditor';
import { getInitialsFromName } from '@core/user';
import ContactIcon from '@phosphor/address-book.svg';
import { useSetContactNameMutation } from '@queries/crm/contacts';
import type { CrmContactResponse } from '@service-storage/generated/schemas/crmContactResponse';
import { Avatar } from '@ui';
import { Show } from 'solid-js';

// Renames overwrite `crm_contacts.name`, which is already team-scoped —
// no global directory involved (unlike company renames).
function TitleEditor(props: { contact: CrmContactResponse }) {
  const renameMutation = useSetContactNameMutation();
  return (
    <InlineTitleEditor
      // Nameless contacts display their email; committing it unchanged
      // is a no-op rather than a save.
      value={props.contact.name ?? props.contact.email}
      placeholder="Contact"
      ariaLabel="Contact name"
      onRename={(name) =>
        renameMutation.mutate({
          contactId: props.contact.id,
          companyId: props.contact.companyId,
          name,
        })
      }
    />
  );
}

export function ContactHeader(props: { contact?: CrmContactResponse }) {
  const showSubtitle = () =>
    props.contact?.name != null && props.contact.name !== props.contact.email;

  // Default avatar mirrors the channel user avatar: initials on a flat circle.
  // Contacts have no photo, so initials come from the name or email.
  const initials = () => {
    const email = props.contact?.email;
    if (!email) return undefined;
    return getInitialsFromName(props.contact?.name, email);
  };

  return (
    <div class="flex items-start gap-3">
      <Avatar size="lg" class="shrink-0">
        <Show
          when={initials()}
          fallback={
            <Avatar.Fallback>
              <ContactIcon class="size-5 text-ink-muted" />
            </Avatar.Fallback>
          }
          keyed
        >
          {(value) => (
            <Avatar.Fallback class="font-semibold">{value}</Avatar.Fallback>
          )}
        </Show>
      </Avatar>
      <div class="flex min-w-0 flex-col gap-1">
        <h1 class="min-w-0 text-xl font-semibold">
          <Show when={props.contact} fallback={'Loading contact…'}>
            {(contact) => <TitleEditor contact={contact()} />}
          </Show>
        </h1>
        <Show when={showSubtitle()}>
          <p class="truncate text-sm text-ink-muted">{props.contact?.email}</p>
        </Show>
      </div>
    </div>
  );
}
